import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/synapse/Field';
import { Icon } from '@/components/synapse/Icon';
import { PasswordInput } from '@/components/synapse/PasswordInput';
import { InitialsAvatar } from '@/components/synapse/Avatar';
import { useAuth } from '@/hooks/useAuth';
import { passwordErrorField, useChangePassword } from '@/hooks/useAccount';
import { translateApiError } from '@/lib/apiError';

/** Matches `changePasswordInput` on the server; a shorter one is refused there. */
const MIN_PASSWORD = 8;

/**
 * The signed-in user's own account: who they are, where they are a member, and
 * the one thing they can change without an admin — their password.
 *
 * Deliberately NOT clinic-scoped in what it shows about the account itself. A
 * user can belong to several clinics and has one password across all of them,
 * so the change form is about the person; the memberships list below is there
 * to make that plain rather than to be managed from here.
 */
export function AccountPage() {
  const { t } = useTranslation();
  const { user, memberships, activeClinic } = useAuth();

  return (
    <div className="mx-auto flex w-full max-w-[640px] flex-col gap-5">
      <header className="flex flex-col gap-1.5">
        <h1 className="text-2xl font-semibold tracking-[-0.02em] sm:text-[26px]">
          {t('account.title')}
        </h1>
        <p className="text-[13.5px] text-muted">{t('account.subtitle')}</p>
      </header>

      <Card className="flex items-center gap-3.5 p-4">
        <InitialsAvatar name={user?.name ?? '?'} size={44} />
        <div className="min-w-0">
          <div className="truncate text-[15px] font-semibold tracking-[-0.01em]">
            {user?.name}
          </div>
          <div className="truncate text-[13px] text-muted">{user?.email}</div>
        </div>
      </Card>

      {memberships.length > 0 && (
        <Card className="flex flex-col gap-2.5 p-4">
          <h2 className="text-[13px] font-semibold tracking-[-0.01em]">
            {t('account.clinics')}
          </h2>
          <ul className="flex flex-col gap-2">
            {memberships.map((m) => (
              <li key={m.id} className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate text-[13.5px]">
                  {m.name}
                  {m.id === activeClinic?.id && (
                    <span className="ml-2 text-[11.5px] text-muted-2">
                      {t('account.currentClinic')}
                    </span>
                  )}
                </span>
                <span className="shrink-0 text-[12.5px] text-muted">
                  {m.roles.map((r) => r.name).join(', ')}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <ChangePasswordCard minLength={MIN_PASSWORD} />
    </div>
  );
}

function ChangePasswordCard({ minLength }: { minLength: number }) {
  const { t } = useTranslation();
  const change = useChangePassword();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  // Which input the server blamed, plus the message to show. Held together so
  // they can never disagree about where the message belongs.
  const [failure, setFailure] = useState<{
    field: 'currentPassword' | 'newPassword' | null;
    message: string;
  } | null>(null);
  const [done, setDone] = useState(false);
  // Purely client-side: the confirm box never reaches the server, so its
  // mismatch is not a failure the API can report.
  const [mismatch, setMismatch] = useState(false);

  const tooShort = next.length > 0 && next.length < minLength;
  const canSubmit =
    current.length > 0 && next.length >= minLength && confirm.length > 0 && !change.isPending;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setDone(false);
    setFailure(null);
    setMismatch(false);
    if (!canSubmit) return;

    if (next !== confirm) {
      setMismatch(true);
      return;
    }

    try {
      await change.mutateAsync({ currentPassword: current, newPassword: next });
      // Clear on success: leaving the old password sitting in a field on a
      // shared reception machine is the whole thing this screen exists to fix.
      setCurrent('');
      setNext('');
      setConfirm('');
      setDone(true);
    } catch (err) {
      setFailure({
        field: passwordErrorField(err),
        message: translateApiError(err, { fallback: t('account.changeFailed') }),
      });
    }
  }

  return (
    <Card className="p-4">
      <h2 className="text-[15px] font-semibold tracking-[-0.01em]">
        {t('account.changePassword')}
      </h2>
      <p className="mt-1 text-[12.5px] text-muted">{t('account.changePasswordBody')}</p>

      <form onSubmit={onSubmit} className="mt-4 flex flex-col gap-3.5">
        <Field
          label={t('account.currentPassword')}
          error={
            failure?.field === 'currentPassword' ? t('account.currentPasswordWrong') : undefined
          }
        >
          <PasswordInput
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            disabled={change.isPending}
          />
        </Field>

        <Field
          label={t('account.newPassword')}
          hint={t('account.passwordHint', { count: minLength })}
          error={
            tooShort
              ? t('account.passwordTooShort', { count: minLength })
              : failure?.field === 'newPassword'
                ? t('account.newPasswordSame')
                : undefined
          }
        >
          <PasswordInput
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            disabled={change.isPending}
          />
        </Field>

        <Field
          label={t('account.confirmPassword')}
          error={mismatch ? t('account.passwordsDoNotMatch') : undefined}
        >
          <PasswordInput
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={change.isPending}
          />
        </Field>

        {/* Failures the server did not pin to a field (offline, 500) still have
            to be seen, so they surface here rather than being swallowed. */}
        {failure && !failure.field && (
          <p className="text-[12.5px] text-destructive" role="alert">
            {failure.message}
          </p>
        )}

        {done && (
          <p className="flex items-center gap-1.5 text-[12.5px] text-success" role="status">
            <Icon name="check_circle" size={15} />
            {t('account.passwordChanged')}
          </p>
        )}

        <div>
          <Button type="submit" disabled={!canSubmit}>
            {change.isPending ? t('account.changing') : t('account.changePassword')}
          </Button>
        </div>
      </form>
    </Card>
  );
}
